import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link } from '@/components/Link';
import { m } from '@/paraglide/messages';
import { cheapest } from '../../lib/catalog';
import { usdCost } from '../../lib/format';
import { quotedRateLabel, quotedRateUsd } from '../../lib/pricing';
import type { ApiDetail, Endpoint } from '../../features/catalog/model';
import { useCatalogLinks } from '../../components/CatalogLinks';
import { Band } from '../../components/editorial/Band';
import { Eyebrow } from '../../components/Eyebrow';

export interface OtherEndpointsProps {
  api: ApiDetail;
  current: Endpoint;
}

/** A rail grid linking to the API's other endpoints, each with its action and
 *  its cheapest price - the same chip the catalog's coverage rails use. */
export const OtherEndpoints = ({ api, current }: OtherEndpointsProps) => {
  const links = useCatalogLinks();
  const others = api.endpoints.filter((e) => e.id !== current.id);
  if (others.length === 0) return null;

  return (
    <Band>
      <div className="band">
        <Eyebrow to={links.api(api.id)}>{m.endpoint_other_eyebrow()}</Eyebrow>
        <h2 className="display-3 mt-4 text-text">{m.endpoint_other_heading({ platform: api.name })}</h2>
        {/* No outer box: the cells carry the rules, so a row that does not fill
            ends where its last endpoint does instead of framing empty cells. */}
        <div className="mt-8 overflow-hidden border-t border-rule">
          <div className="-mr-px grid grid-cols-1 s480:grid-cols-2 lg:grid-cols-3">
            {others.map((e) => {
              const price = cheapest(e).price;
              return (
                <Link
                  key={e.id}
                  to={links.endpoint(api.id, e.id)}
                  className="group flex min-w-0 flex-col gap-1 border-b border-r border-rule px-4 py-3.5 transition-colors hover:bg-wash"
                >
                  <span className="truncate text-14 font-medium group-hover:text-accent">
                    {e.action}
                  </span>
                  <span className="mono truncate text-12 text-text-3">
                    <ParaglideMessage
                      message={m.endpoint_other_from}
                      inputs={{ amount: usdCost(quotedRateUsd(price)), rate: quotedRateLabel(price) }}
                      markup={{
                        price: ({ children }) => (
                          <span className={price.metered ? 'text-meter' : 'text-accent'}>{children}</span>
                        ),
                      }}
                    />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </Band>
  );
};
